import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { prepareAction, executeAction, getAction } from '../../../../services/ReconciliationActionService';

// Keep the displayed immutable action across timeouts; a retry never creates a
// replacement payment. Edits explicitly discard the preview and prepare anew.
export default function useInvoiceSettlement({ companyId, line, onSettled, storageNamespace = 'bank-settlement' }) {
    const [plan, setPlan] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [unknown, setUnknown] = useState(false);
    const executeId = useRef(null);
    const storageKey = `${storageNamespace}:${companyId}:${line.id}`;
    const forget = () => { try { sessionStorage.removeItem(storageKey); } catch (_) {} };
    useEffect(() => {
        let active = true;
        let saved;
        try { saved = JSON.parse(sessionStorage.getItem(storageKey)); } catch (_) {}
        if (!saved) return undefined;
        executeId.current = saved.executeId;
        setBusy(true); setUnknown(true);
        getAction(companyId, saved.actionId).then(live => {
            if (!active) return;
            setPlan(live.plan); setResult(live.result);
            if (live.result || (live.eligibility?.stale && !saved.attempted)) {
                setUnknown(false); forget();
                if (!live.result) { setPlan(null); setError('Underlaget har ändrats. Förhandsgranska igen.'); }
            } else setUnknown(Boolean(saved.attempted));
        }).catch(() => { if (active) setError('En tidigare betalning behöver kontrolleras. Ladda om för att hämta resultatet.'); })
            .finally(() => { if (active) setBusy(false); });
        return () => { active = false; };
        // One workspace per company and bank line.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, storageKey]);
    const clear = () => { if (!busy && !unknown) { forget(); setPlan(null); setResult(null); setError(null); } };
    const prepare = async (invoice, bank, payment = null, accountingInputs = null) => {
        if (busy || unknown) return;
        setBusy(true); setError(null); setPlan(null); setResult(null);
        try {
            const type = payment ? 'link_existing_payment' : `register_${invoice.side}_payment`;
            const request = { contract: 'bank-reconciliation/1', request_id: uuid(), statement_id: line.id,
                expected_bank_revision: bank.revision, expected_bank_generation: bank.generation, type,
                target: payment ? { side: invoice.side, invoice_id: invoice.id, payment_id: payment.payment_id } : { invoice_id: invoice.id },
                evidence_ids: [], ...(type === 'register_supplier_payment' ? { accounting_inputs: accountingInputs } : {}) };
            const prepared = await prepareAction(companyId, request);
            executeId.current = uuid(); setPlan(prepared);
            try { sessionStorage.setItem(storageKey, JSON.stringify({actionId:prepared.action_id, executeId:executeId.current, attempted:false})); } catch (_) {}
        } catch (e) { setError(e.response?.data?.message || 'Kunde inte förhandsgranska betalningen.'); }
        finally { setBusy(false); }
    };
    const prepareRequest = async request => {
        if(busy || unknown)return;
        setBusy(true);setError(null);
        try {
            const prepared=await prepareAction(companyId,{contract:'bank-reconciliation/1',request_id:uuid(),statement_id:line.id,...request});
            executeId.current=uuid();setPlan(prepared);setResult(null);
            try{sessionStorage.setItem(storageKey,JSON.stringify({actionId:prepared.action_id,executeId:executeId.current,attempted:false}));}catch(_){}
        }catch(e){setError(e.response?.data?.message || 'Kunde inte förhandsgranska.');}
        finally{setBusy(false);}
    };
    const execute = async () => {
        if (!plan || busy) return;
        setBusy(true); setError(null);
        try {
            try { sessionStorage.setItem(storageKey, JSON.stringify({actionId:plan.action_id, executeId:executeId.current, attempted:true})); } catch (_) {}
            const committed = await executeAction(companyId, plan.action_id, { plan_hash: plan.plan_hash, request_id: executeId.current });
            setResult(committed); setUnknown(false); forget(); onSettled?.();
        } catch (e) {
            // A lost HTTP response says nothing about the financial commit.
            if (!e.response || e.response.status >= 500) {
                setUnknown(true);
                try {
                    const live = await getAction(companyId, plan.action_id);
                    if (live.result) { setResult(live.result); setUnknown(false); forget(); onSettled?.(); return; }
                } catch (_) { /* Preserve action identity for the next lookup/retry. */ }
                setError('Resultatet kunde inte bekräftas. Försök igen för att kontrollera samma betalning.');
            } else {
                setError(e.response?.data?.message || 'Betalningen kunde inte utföras.');
                if (e.response.status === 409 || e.response.status === 422) { setUnknown(false); forget(); setPlan(null); onSettled?.(); }
            }
        } finally { setBusy(false); }
    };
    return { plan, result, error, busy, unknown, prepare, prepareRequest, execute, clear };
}
